import { useCallback, useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Pen, RotateCcw, Trash2, UserPlus } from 'lucide-react'
import { toast } from 'react-toastify'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import {
  deleteUserBranchAssignment,
  getBranchUsers,
  getManagerInvitations,
  inviteUser,
  patchUserRole,
} from '../api/users'
import { formatApiError } from '../lib/apiErrorMessage'
import { useAuthStore } from '../store/authStore'
import useManagerStore from '../store/managerStore'
import { canAccess, normalizeRole, RBAC_MODULES } from '../lib/rbac'

const ROLE_STYLE = {
  admin: { bg: '#fff2e2', color: '#a85a00' },
  manager: { bg: '#e8f2ff', color: '#0d5db8' },
  supervisor: { bg: '#efe9ff', color: '#6a35cc' },
  receptionist: { bg: '#e8f7ee', color: '#1f7a43' },
}

const INVITATION_STATUS_STYLE = {
  pending: { bg: '#fff8e1', color: '#f57f17' },
  expired: { bg: '#fff2f0', color: '#c62828' },
  rejected: { bg: '#f3f4f6', color: '#4b5563' },
}

const MEMBERS_PAGE_SIZE = 5
const INVITATIONS_PAGE_SIZE = 5

function RolePill({ role, t }) {
  const style = ROLE_STYLE[role] || { bg: '#f3f4f6', color: '#4b5563' }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        textTransform: 'capitalize',
      }}
    >
      {t(`adminPermissionsPage.roles.${role}`, { defaultValue: role })}
    </span>
  )
}

function InvitationStatusPill({ status, t }) {
  const style = INVITATION_STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#4b5563' }
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        background: style.bg,
        color: style.color,
        textTransform: 'capitalize',
      }}
    >
      {t(`adminPermissionsPage.invitationStatus.${status}`, { defaultValue: status })}
    </span>
  )
}

function PaginationControls({ currentPage, totalPages, onPrev, onNext, t }) {
  return (
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
      <button
        type="button"
        onClick={onPrev}
        disabled={currentPage <= 1}
        style={{
          border: '1px solid #d9deea',
          borderRadius: 8,
          padding: '6px 10px',
          background: 'white',
          color: currentPage <= 1 ? '#98a2b3' : '#344054',
          fontSize: 12,
          fontWeight: 600,
          cursor: currentPage <= 1 ? 'not-allowed' : 'pointer',
        }}
      >
        {t('adminPermissionsPage.pagination.prev')}
      </button>
      <span style={{ fontSize: 12, color: '#667085' }}>
        {t('adminPermissionsPage.pagination.page', { current: currentPage, total: totalPages })}
      </span>
      <button
        type="button"
        onClick={onNext}
        disabled={currentPage >= totalPages}
        style={{
          border: '1px solid #d9deea',
          borderRadius: 8,
          padding: '6px 10px',
          background: 'white',
          color: currentPage >= totalPages ? '#98a2b3' : '#344054',
          fontSize: 12,
          fontWeight: 600,
          cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer',
        }}
      >
        {t('adminPermissionsPage.pagination.next')}
      </button>
    </div>
  )
}

export default function AdminPermissionsPage() {
  const { t } = useTranslation()
  const authUser = useAuthStore((s) => s.user)
  const orgId = useAuthStore((s) => s.orgId)
  const branches = useAuthStore((s) => s.branches)
  const storeRole = useManagerStore((s) => s.role)
  const tenantId = useManagerStore((s) => s.tenantId)
  const [members, setMembers] = useState([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [membersError, setMembersError] = useState('')
  const [invitations, setInvitations] = useState([])
  const [invitationsCount, setInvitationsCount] = useState(0)
  const [invitationsSkip, setInvitationsSkip] = useState(0)
  const [invitationsLoading, setInvitationsLoading] = useState(false)
  const [invitationsError, setInvitationsError] = useState('')
  const [invitationsReloadKey, setInvitationsReloadKey] = useState(0)
  const [membersPage, setMembersPage] = useState(1)
  const [invitationsPage, setInvitationsPage] = useState(1)
  const [draftRole, setDraftRole] = useState('manager')
  const [roleEditOpen, setRoleEditOpen] = useState(false)
  const [roleEditMember, setRoleEditMember] = useState(null)
  const [roleEditSaving, setRoleEditSaving] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('manager')
  const [inviteSending, setInviteSending] = useState(false)
  const [resendingInviteId, setResendingInviteId] = useState(null)
  const [deletingMemberId, setDeletingMemberId] = useState(null)
  const [removeConfirmMember, setRemoveConfirmMember] = useState(null)
  const isDesktop = window.innerWidth >= 1024

  const supportedRoleOptions = useMemo(
    () => ['admin', 'manager', 'supervisor', 'receptionist'],
    []
  )
  const assignableRoleOptions = useMemo(
    () => ['manager', 'supervisor', 'receptionist'],
    []
  )
  const resolveRole = useCallback(
    (rawRole, fallback = 'receptionist') => {
      const source = String(rawRole || '').trim()
      if (!source) return fallback
      const normalized = normalizeRole(source)
      return supportedRoleOptions.includes(normalized) ? normalized : fallback
    },
    [supportedRoleOptions]
  )
  const activeBranch = useMemo(
    () =>
      branches.find((branch) => branch?.tenant_id && branch.tenant_id === tenantId) ??
      branches[0] ??
      null,
    [branches, tenantId]
  )
  const resolvedCurrentName =
    authUser?.name ||
    authUser?.full_name ||
    authUser?.fullName ||
    (authUser?.username && authUser?.username !== authUser?.email
      ? authUser.username
      : '') ||
    t('common.userFallback')
  const normalizedCurrentEmail = (authUser?.email || '').trim().toLowerCase()
  const normalizedCurrentName = resolvedCurrentName.trim().toLowerCase()
  const normalizedCurrentRole = normalizeRole(storeRole)
  const canAccessAdminPermissions = canAccess(normalizedCurrentRole, RBAC_MODULES.adminPermissions)
  const currentUser = useMemo(() => {
    const normalizedRole = resolveRole(storeRole, 'admin')
    return {
      name: resolvedCurrentName,
      email: authUser?.email || '—',
      role: normalizedRole,
    }
  }, [authUser, resolvedCurrentName, resolveRole, storeRole])
  function isCurrentUserMember(member) {
    const memberEmail = (member.email || '').trim().toLowerCase()
    const memberName = (member.name || '').trim().toLowerCase()
    return (
      (normalizedCurrentEmail && memberEmail === normalizedCurrentEmail) ||
      (!normalizedCurrentEmail && normalizedCurrentName && memberName === normalizedCurrentName)
    )
  }
  const listMembers = members
    .filter((member) => member.isActive)
    .filter((member) => !isCurrentUserMember(member))
  const membersVisibleTotal = listMembers.length
  const membersTotalPages = Math.max(1, Math.ceil(membersVisibleTotal / MEMBERS_PAGE_SIZE))
  const normalizedMembersPage = Math.min(membersPage, membersTotalPages)
  const membersStartIndex = (normalizedMembersPage - 1) * MEMBERS_PAGE_SIZE
  const pagedMembers = listMembers.slice(membersStartIndex, membersStartIndex + MEMBERS_PAGE_SIZE)

  const invitationsTotalPages = Math.max(1, Math.ceil(invitationsCount / INVITATIONS_PAGE_SIZE))
  const normalizedInvitationsPage = Math.min(invitationsPage, invitationsTotalPages)
  const invitationsStartIndex = invitationsSkip
  const pagedInvitations = invitations

  useEffect(() => {
    let active = true

    async function fetchMembers() {
      if (!activeBranch?.id) {
        if (!active) return
        setMembers([])
        setMembersError('')
        setMembersLoading(false)
        return
      }

      setMembersLoading(true)
      setMembersError('')
      try {
        const data = await getBranchUsers(activeBranch.id, 0, 10000, true)
        if (!active) return
        const normalizedMembers = (Array.isArray(data?.items) ? data.items : []).map((member, index) => {
          const roleFromAssignment = resolveRole(member?.assignment?.role, '')
          const roleFromOrg = resolveRole(member?.org_role, 'receptionist')
          const statusFromAssignment = String(member?.assignment?.status || '').toLowerCase()
          const statusFromUser = String(member?.user_status || '').toLowerCase()
          const isActive = Boolean(member?.is_active)
          return {
            id: member?.id ?? `user-${index}`,
            name: member?.name || member?.email || t('common.userFallback'),
            role: roleFromAssignment || roleFromOrg,
            email: member?.email || '—',
            status: statusFromAssignment || statusFromUser || (isActive ? 'active' : 'inactive'),
            isActive,
          }
        })
        setMembers(normalizedMembers)
      } catch (error) {
        if (!active) return
        setMembers([])
        setMembersError(error?.message || t('common.requestFailed'))
        toast.error(t('common.requestFailed'))
      } finally {
        if (active) setMembersLoading(false)
      }
    }

    void fetchMembers()
    return () => {
      active = false
    }
  }, [activeBranch?.id, resolveRole, t])

  useEffect(() => {
    setMembersPage(1)
  }, [activeBranch?.id])

  useEffect(() => {
    if (membersPage > membersTotalPages) {
      setMembersPage(membersTotalPages)
    }
  }, [membersPage, membersTotalPages])

  useEffect(() => {
    setInvitationsPage(1)
  }, [activeBranch?.id])

  useEffect(() => {
    let active = true

    async function fetchInvitations() {
      if (!activeBranch?.id) {
        if (!active) return
        setInvitations([])
        setInvitationsCount(0)
        setInvitationsSkip(0)
        setInvitationsError('')
        setInvitationsLoading(false)
        return
      }

      setInvitationsLoading(true)
      setInvitationsError('')
      const skip = (invitationsPage - 1) * INVITATIONS_PAGE_SIZE
      try {
        const data = await getManagerInvitations(activeBranch.id, skip, INVITATIONS_PAGE_SIZE)
        if (!active) return
        const normalizedInvitations = (Array.isArray(data?.items) ? data.items : []).map((item, index) => {
          const roleFromApi = resolveRole(item?.role, 'receptionist')
          const statusFromApi = String(item?.invite_status || item?.status || '').toLowerCase()
          return {
            id: item?.id ?? item?.invite_id ?? `${item?.email || 'invitation'}-${skip + index}`,
            email: item?.email || t('adminPermissionsPage.emptyValue'),
            role: roleFromApi,
            status: statusFromApi || 'pending',
          }
        })
        setInvitations(normalizedInvitations)
        setInvitationsCount(Number.isFinite(Number(data?.count)) ? Number(data.count) : 0)
        setInvitationsSkip(Number.isFinite(Number(data?.skip)) ? Number(data.skip) : skip)
      } catch (error) {
        if (!active) return
        setInvitations([])
        setInvitationsCount(0)
        setInvitationsSkip(skip)
        setInvitationsError(error?.message || t('common.requestFailed'))
      } finally {
        if (active) setInvitationsLoading(false)
      }
    }

    void fetchInvitations()
    return () => {
      active = false
    }
  }, [activeBranch?.id, invitationsPage, invitationsReloadKey, resolveRole, t])

  useEffect(() => {
    if (invitationsPage > invitationsTotalPages) {
      setInvitationsPage(invitationsTotalPages)
    }
  }, [invitationsPage, invitationsTotalPages])

  function openRoleEdit(member) {
    setRoleEditMember(member)
    setDraftRole(resolveRole(member?.role, 'manager'))
    setRoleEditOpen(true)
  }

  function closeRoleEdit() {
    if (roleEditSaving) return
    setRoleEditOpen(false)
    setRoleEditMember(null)
    setDraftRole('manager')
  }

  async function saveRole() {
    const memberId = roleEditMember?.id
    if (!orgId) {
      toast.error(t('adminPermissionsPage.invite.errors.missingOrg'))
      return
    }
    if (!activeBranch?.id || !memberId || !assignableRoleOptions.includes(draftRole)) {
      toast.error(t('common.requestFailed'))
      return
    }
    setRoleEditSaving(true)
    try {
      await patchUserRole(orgId, memberId, activeBranch.id, draftRole)
      setMembers((prev) =>
        prev.map((member) =>
          member.id === memberId ? { ...member, role: draftRole } : member
        )
      )
      setRoleEditOpen(false)
      setRoleEditMember(null)
      setDraftRole('manager')
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'save_member_role' },
        extra: { orgId, memberId, branchId: activeBranch?.id, role: draftRole },
      })
      toast.error(t('common.requestFailed'))
    } finally {
      setRoleEditSaving(false)
    }
  }

  async function handleSendInvite() {
    const email = inviteEmail.trim()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      toast.error(t('adminPermissionsPage.invite.errors.invalidEmail'))
      return
    }
    if (!orgId) {
      toast.error(t('adminPermissionsPage.invite.errors.missingOrg'))
      return
    }
    if (!activeBranch?.id) {
      toast.error(t('common.requestFailed'))
      return
    }

    setInviteSending(true)
    try {
      await inviteUser(orgId, { email, role: inviteRole, branch_id: activeBranch.id })
      setInvitationsPage(1)
      setInvitationsReloadKey((prev) => prev + 1)
      setInviteOpen(false)
      setInviteEmail('')
      setInviteRole('receptionist')
      toast.success(t('adminPermissionsPage.invite.success'))
    } catch (err) {
      const fallbackMessage = t('adminPermissionsPage.invite.errors.sendFailed')
      const backendMessage = err?.response?.data?.message
      const inviteErrorMessage =
        typeof backendMessage === 'string' && backendMessage.trim()
          ? backendMessage.trim()
          : formatApiError(err, fallbackMessage)
      toast.error(inviteErrorMessage)
    } finally {
      setInviteSending(false)
    }
  }

  async function handleResendInvite(invitation) {
    const email = invitation?.email?.trim()
    if (!email || email === t('adminPermissionsPage.emptyValue')) {
      toast.error(t('adminPermissionsPage.invite.errors.invalidEmail'))
      return
    }
    if (!orgId) {
      toast.error(t('adminPermissionsPage.invite.errors.missingOrg'))
      return
    }
    if (!activeBranch?.id) {
      toast.error(t('common.requestFailed'))
      return
    }

    const inviteId = invitation?.id
    if (!inviteId || resendingInviteId === inviteId) return

    setResendingInviteId(inviteId)
    try {
      await inviteUser(orgId, {
        email,
        role: invitation?.role || 'receptionist',
        branch_id: activeBranch.id,
      })
      setInvitationsReloadKey((prev) => prev + 1)
      toast.success(t('adminPermissionsPage.invite.resendSuccess'))
    } catch (err) {
      const fallbackMessage = t('adminPermissionsPage.invite.errors.resendFailed')
      const backendMessage = err?.response?.data?.message
      const resendErrorMessage =
        typeof backendMessage === 'string' && backendMessage.trim()
          ? backendMessage.trim()
          : formatApiError(err, fallbackMessage)
      toast.error(resendErrorMessage)
    } finally {
      setResendingInviteId((prev) => (prev === inviteId ? null : prev))
    }
  }

  function openRemoveMemberConfirm(member) {
    if (!member?.id) return
    setRemoveConfirmMember(member)
  }

  function closeRemoveMemberConfirm() {
    if (deletingMemberId && removeConfirmMember?.id === deletingMemberId) return
    setRemoveConfirmMember(null)
  }

  async function confirmRemoveMemberFromBranch() {
    const memberId = removeConfirmMember?.id
    if (!orgId) {
      toast.error(t('adminPermissionsPage.invite.errors.missingOrg'))
      return
    }
    if (!activeBranch?.id || !memberId) {
      toast.error(t('common.requestFailed'))
      return
    }

    setDeletingMemberId(memberId)
    try {
      await deleteUserBranchAssignment(orgId, memberId, activeBranch.id)
      setMembers((prev) => prev.filter((item) => item.id !== memberId))
      setRemoveConfirmMember(null)
      toast.success(t('adminPermissionsPage.actions.deleteSuccess'))
    } catch (err) {
      const fallbackMessage = t('adminPermissionsPage.actions.deleteFailed')
      const backendMessage = err?.response?.data?.message
      const deleteErrorMessage =
        typeof backendMessage === 'string' && backendMessage.trim()
          ? backendMessage.trim()
          : formatApiError(err, fallbackMessage)
      toast.error(deleteErrorMessage)
    } finally {
      setDeletingMemberId((prev) => (prev === memberId ? null : prev))
    }
  }

  if (!canAccessAdminPermissions) {
    return (
      <ResponsiveShell title={t('adminPermissionsPage.title')}>
        <div style={{ background: 'white', border: '1px solid #e7e9ee', borderRadius: 14, padding: 14 }}>
          {t('common.unauthorized', { defaultValue: 'You do not have permission to access this feature.' })}
        </div>
      </ResponsiveShell>
    )
  }

  const content = (
    <div style={{ padding: isDesktop ? '4px 0 20px' : '0 0 14px' }}>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: isDesktop ? 28 : 22, fontWeight: 800, color: '#101828' }}>
          {t('adminPermissionsPage.title')}
        </h1>
        <p style={{ margin: '6px 0 0', color: '#667085', fontSize: 14 }}>
          {t('adminPermissionsPage.totalMembers', { count: membersVisibleTotal })}
        </p>
      </div>

      <div
        style={{
          background: 'white',
          border: '1px solid #e7e9ee',
          borderRadius: 16,
          padding: isDesktop ? 14 : 12,
          marginBottom: 12,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 700, color: '#7a8091', marginBottom: 8 }}>
          {t('adminPermissionsPage.currentUser')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: '#eef0ff',
                color: '#4f46e5',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {currentUser.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>{currentUser.name}</div>
              <div style={{ fontSize: 13, color: '#667085' }}>{currentUser.email}</div>
            </div>
          </div>
          <RolePill role={currentUser.role} t={t} />
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 700, color: '#7a8091', marginBottom: 8 }}>
        {t('adminPermissionsPage.userList')}
      </div>

      {membersLoading ? (
        <div
          style={{
            background: 'white',
            border: '1px solid #e7e9ee',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#667085',
            fontSize: 14,
          }}
        >
          {t('common.loading', { defaultValue: 'Loading...' })}
        </div>
      ) : membersError ? (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffd1cd',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#b42318',
            fontSize: 14,
          }}
        >
          {membersError}
        </div>
      ) : pagedMembers.length === 0 ? (
        <div
          style={{
            background: 'white',
            border: '1px solid #e7e9ee',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#667085',
            fontSize: 14,
          }}
        >
          {t('adminPermissionsPage.emptyMembers', { defaultValue: 'No users found.' })}
        </div>
      ) : isDesktop ? (
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e7e9ee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  t('adminPermissionsPage.columns.no'),
                  t('adminPermissionsPage.columns.name'),
                  t('adminPermissionsPage.columns.role'),
                  t('adminPermissionsPage.columns.email'),
                  t('adminPermissionsPage.columns.actions'),
                ].map((head) => (
                  <th
                    key={head}
                    style={{
                      textAlign: 'left',
                      padding: '14px 14px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#7a8091',
                      borderBottom: '1px solid #edf0f4',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedMembers.map((member, index) => {
                const isCurrentMember = isCurrentUserMember(member)
                const isDeletingMember = deletingMemberId === member.id
                return (
                  <tr key={member.id} style={{ borderBottom: index < pagedMembers.length - 1 ? '1px solid #f1f3f6' : 'none' }}>
                    <td style={{ padding: '14px', color: '#4f46e5', fontSize: 15, fontWeight: 700 }}>{membersStartIndex + index + 1}</td>
                    <td style={{ padding: '14px', minWidth: 210 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div
                          style={{
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            background: '#eef0ff',
                            color: '#4f46e5',
                            fontSize: 13,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 400, color: '#111827' }}>{member.name}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px', minWidth: 190 }}>
                      <RolePill role={member.role} t={t} />
                    </td>
                    <td style={{ padding: '14px', fontSize: 14, color: '#111827', minWidth: 180 }}>{member.email}</td>
                    <td style={{ padding: '14px', minWidth: 120 }}>
                      {!isCurrentMember && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => openRoleEdit(member)}
                            className="text-[#98a2b3] hover:text-[#1535c8] transition-colors"
                            disabled={isDeletingMember}
                            style={{
                              border: 'none',
                              padding: 0,
                              background: 'transparent',
                              cursor: isDeletingMember ? 'not-allowed' : 'pointer',
                              opacity: isDeletingMember ? 0.5 : 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label={t('adminPermissionsPage.actions.editRole')}
                            title={t('adminPermissionsPage.actions.editRole')}
                          >
                            <Pen size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => openRemoveMemberConfirm(member)}
                            className="text-[#f04438] hover:text-[#b42318] transition-colors"
                            disabled={isDeletingMember}
                            style={{
                              border: 'none',
                              padding: 0,
                              background: 'transparent',
                              cursor: isDeletingMember ? 'not-allowed' : 'pointer',
                              opacity: isDeletingMember ? 0.5 : 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            aria-label={t('adminPermissionsPage.actions.deleteAssignment')}
                            title={t('adminPermissionsPage.actions.deleteAssignment')}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pagedMembers.map((member, index) => {
            const isCurrentMember = isCurrentUserMember(member)
            const isDeletingMember = deletingMemberId === member.id
            return (
              <div key={member.id} style={{ background: 'white', border: '1px solid #e7e9ee', borderRadius: 14, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#5b61d6', fontWeight: 700 }}>#{membersStartIndex + index + 1}</div>
                    <div style={{ marginTop: 4, fontWeight: 400, fontSize: 14, color: '#111827' }}>{member.name}</div>
                  </div>
                  {!isCurrentMember ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => openRoleEdit(member)}
                        className="text-[#98a2b3] hover:text-[#1535c8] transition-colors"
                        disabled={isDeletingMember}
                        style={{
                          border: 'none',
                          padding: 0,
                          background: 'transparent',
                          cursor: isDeletingMember ? 'not-allowed' : 'pointer',
                          opacity: isDeletingMember ? 0.5 : 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        aria-label={t('adminPermissionsPage.actions.editRole')}
                        title={t('adminPermissionsPage.actions.editRole')}
                      >
                        <Pen size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => openRemoveMemberConfirm(member)}
                        className="text-[#f04438] hover:text-[#b42318] transition-colors"
                        disabled={isDeletingMember}
                        style={{
                          border: 'none',
                          padding: 0,
                          background: 'transparent',
                          cursor: isDeletingMember ? 'not-allowed' : 'pointer',
                          opacity: isDeletingMember ? 0.5 : 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        aria-label={t('adminPermissionsPage.actions.deleteAssignment')}
                        title={t('adminPermissionsPage.actions.deleteAssignment')}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : null}
                </div>

                <div style={{ marginTop: 10 }}>
                  <RolePill role={member.role} t={t} />
                </div>

                <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                  <div style={{ fontSize: 13, color: '#111827' }}>{member.email}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}
      {!membersLoading && !membersError && pagedMembers.length > 0 ? (
        <PaginationControls
          currentPage={normalizedMembersPage}
          totalPages={membersTotalPages}
          onPrev={() => setMembersPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setMembersPage((prev) => Math.min(membersTotalPages, prev + 1))}
          t={t}
        />
      ) : null}

      <div style={{ fontSize: 12, fontWeight: 700, color: '#7a8091', marginTop: 14, marginBottom: 8 }}>
        {t('adminPermissionsPage.invitationsStatusTitle')}
      </div>
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={() => setInviteOpen(true)}
          style={{
            border: 'none',
            borderRadius: 10,
            padding: '8px 12px',
            background: '#1535c8',
            color: 'white',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <UserPlus size={15} />
          {t('adminPermissionsPage.invite.cta')}
        </button>
      </div>

      {invitationsLoading ? (
        <div
          style={{
            background: 'white',
            border: '1px solid #e7e9ee',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#667085',
            fontSize: 14,
          }}
        >
          {t('common.loading', { defaultValue: 'Loading...' })}
        </div>
      ) : invitationsError ? (
        <div
          style={{
            background: '#fff2f0',
            border: '1px solid #ffd1cd',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#b42318',
            fontSize: 14,
          }}
        >
          {invitationsError}
        </div>
      ) : pagedInvitations.length === 0 ? (
        <div
          style={{
            background: 'white',
            border: '1px solid #e7e9ee',
            borderRadius: 16,
            padding: isDesktop ? 14 : 12,
            color: '#667085',
            fontSize: 14,
          }}
        >
          {t('adminPermissionsPage.emptyInvitations', { defaultValue: 'No invitations found.' })}
        </div>
      ) : isDesktop ? (
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e7e9ee', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                {[
                  t('adminPermissionsPage.columns.no'),
                  t('adminPermissionsPage.invitationColumns.email'),
                  t('adminPermissionsPage.invitationColumns.role'),
                  t('adminPermissionsPage.invitationColumns.status'),
                  t('adminPermissionsPage.columns.actions'),
                ].map((head) => (
                  <th
                    key={head}
                    style={{
                      textAlign: 'left',
                      padding: '14px 14px',
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#7a8091',
                      borderBottom: '1px solid #edf0f4',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedInvitations.map((invitation, index) => (
                <tr
                  key={invitation.id}
                  style={{ borderBottom: index < pagedInvitations.length - 1 ? '1px solid #f1f3f6' : 'none' }}
                >
                  <td style={{ padding: '14px', color: '#4f46e5', fontSize: 15, fontWeight: 700 }}>
                    {invitationsStartIndex + index + 1}
                  </td>
                  <td style={{ padding: '14px', fontSize: 14, color: '#111827' }}>{invitation.email}</td>
                  <td style={{ padding: '14px', minWidth: 190 }}>
                    <RolePill role={invitation.role} t={t} />
                  </td>
                  <td style={{ padding: '14px', minWidth: 170 }}>
                    <InvitationStatusPill status={invitation.status} t={t} />
                  </td>
                  <td style={{ padding: '14px', minWidth: 110 }}>
                    {invitation.status === 'expired' ? (
                      <button
                        type="button"
                        onClick={() => handleResendInvite(invitation)}
                        disabled={resendingInviteId === invitation.id}
                        className="text-[#98a2b3] hover:text-[#1535c8] transition-colors"
                        style={{
                          border: 'none',
                          padding: 0,
                          background: 'transparent',
                          cursor: resendingInviteId === invitation.id ? 'not-allowed' : 'pointer',
                          opacity: resendingInviteId === invitation.id ? 0.5 : 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        aria-label={
                          resendingInviteId === invitation.id
                            ? t('adminPermissionsPage.invite.resending')
                            : t('adminPermissionsPage.invite.resend')
                        }
                        title={
                          resendingInviteId === invitation.id
                            ? t('adminPermissionsPage.invite.resending')
                            : t('adminPermissionsPage.invite.resend')
                        }
                      >
                        <RotateCcw size={16} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pagedInvitations.map((invitation, index) => (
            <div
              key={invitation.id}
              style={{ background: 'white', border: '1px solid #e7e9ee', borderRadius: 14, padding: 12 }}
            >
              <div style={{ fontSize: 12, color: '#5b61d6', fontWeight: 700 }}>#{invitationsStartIndex + index + 1}</div>
              <div style={{ fontSize: 13, color: '#111827' }}>{invitation.email}</div>
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <RolePill role={invitation.role} t={t} />
                <InvitationStatusPill status={invitation.status} t={t} />
                {invitation.status === 'expired' ? (
                  <button
                    type="button"
                    onClick={() => handleResendInvite(invitation)}
                    disabled={resendingInviteId === invitation.id}
                    className="text-[#98a2b3] hover:text-[#1535c8] transition-colors"
                    style={{
                      border: 'none',
                      padding: 0,
                      background: 'transparent',
                      cursor: resendingInviteId === invitation.id ? 'not-allowed' : 'pointer',
                      opacity: resendingInviteId === invitation.id ? 0.5 : 1,
                      marginLeft: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    aria-label={
                      resendingInviteId === invitation.id
                        ? t('adminPermissionsPage.invite.resending')
                        : t('adminPermissionsPage.invite.resend')
                    }
                    title={
                      resendingInviteId === invitation.id
                        ? t('adminPermissionsPage.invite.resending')
                        : t('adminPermissionsPage.invite.resend')
                    }
                  >
                    <RotateCcw size={16} />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {!invitationsLoading && !invitationsError && pagedInvitations.length > 0 ? (
        <PaginationControls
          currentPage={normalizedInvitationsPage}
          totalPages={invitationsTotalPages}
          onPrev={() => setInvitationsPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setInvitationsPage((prev) => Math.min(invitationsTotalPages, prev + 1))}
          t={t}
        />
      ) : null}

      {removeConfirmMember ? (
        <div
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && deletingMemberId !== removeConfirmMember.id) closeRemoveMemberConfirm()
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: 'white',
              borderRadius: 14,
              border: '1px solid #e7e9ee',
              padding: 16,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, color: '#101828' }}>
              {t('adminPermissionsPage.actions.deleteAssignment')}
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: '#667085' }}>
              {t('adminPermissionsPage.actions.deleteConfirm', {
                name: removeConfirmMember?.name || t('common.userFallback'),
              })}
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={closeRemoveMemberConfirm}
                disabled={deletingMemberId === removeConfirmMember.id}
                style={{
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: 'white',
                  color: '#475467',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: deletingMemberId === removeConfirmMember.id ? 'not-allowed' : 'pointer',
                }}
              >
                {t('adminPermissionsPage.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={confirmRemoveMemberFromBranch}
                disabled={deletingMemberId === removeConfirmMember.id}
                style={{
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: '#f04438',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: deletingMemberId === removeConfirmMember.id ? 'not-allowed' : 'pointer',
                  opacity: deletingMemberId === removeConfirmMember.id ? 0.8 : 1,
                }}
              >
                {deletingMemberId === removeConfirmMember.id
                  ? t('common.loading', { defaultValue: 'Loading...' })
                  : t('common.delete', { defaultValue: 'Delete' })}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {roleEditOpen ? (
        <div
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeRoleEdit()
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: 'white',
              borderRadius: 14,
              border: '1px solid #e7e9ee',
              padding: 16,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, color: '#101828' }}>
              {t('adminPermissionsPage.actions.editRole')}
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: '#667085' }}>{roleEditMember?.name || '—'}</div>
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#667085', marginBottom: 6 }}>
                {t('adminPermissionsPage.columns.role')}
              </label>
              <select
                value={draftRole}
                onChange={(e) => setDraftRole(e.target.value)}
                disabled={roleEditSaving}
                style={{
                  width: '100%',
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '9px 11px',
                  fontSize: 14,
                  color: '#111827',
                  background: 'white',
                }}
              >
                {assignableRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {t(`adminPermissionsPage.roles.${role}`)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={closeRoleEdit}
                disabled={roleEditSaving}
                style={{
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: 'white',
                  color: '#475467',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: roleEditSaving ? 'not-allowed' : 'pointer',
                }}
              >
                {t('adminPermissionsPage.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={saveRole}
                disabled={roleEditSaving}
                style={{
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: '#1535c8',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: roleEditSaving ? 'not-allowed' : 'pointer',
                  opacity: roleEditSaving ? 0.8 : 1,
                }}
              >
                {roleEditSaving
                  ? t('common.loading', { defaultValue: 'Loading...' })
                  : t('adminPermissionsPage.actions.save')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {inviteOpen ? (
        <div
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !inviteSending) setInviteOpen(false)
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.38)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: 420,
              background: 'white',
              borderRadius: 14,
              border: '1px solid #e7e9ee',
              padding: 16,
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 800, color: '#101828' }}>
              {t('adminPermissionsPage.invite.title')}
            </div>
            <div style={{ marginTop: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#667085', marginBottom: 6 }}>
                {t('adminPermissionsPage.invite.emailLabel')}
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t('adminPermissionsPage.invite.emailPlaceholder')}
                disabled={inviteSending}
                style={{
                  width: '100%',
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '9px 11px',
                  fontSize: 14,
                  color: '#111827',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#667085', marginBottom: 6 }}>
                {t('adminPermissionsPage.invite.roleLabel')}
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                disabled={inviteSending}
                style={{
                  width: '100%',
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '9px 11px',
                  fontSize: 14,
                  color: '#111827',
                  background: 'white',
                }}
              >
                {assignableRoleOptions.map((role) => (
                  <option key={role} value={role}>
                    {t(`adminPermissionsPage.roles.${role}`)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setInviteOpen(false)}
                disabled={inviteSending}
                style={{
                  border: '1px solid #d0d5dd',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: 'white',
                  color: '#475467',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: inviteSending ? 'not-allowed' : 'pointer',
                }}
              >
                {t('adminPermissionsPage.actions.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSendInvite}
                disabled={inviteSending}
                style={{
                  border: 'none',
                  borderRadius: 10,
                  padding: '8px 12px',
                  background: '#1535c8',
                  color: 'white',
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: inviteSending ? 'not-allowed' : 'pointer',
                  opacity: inviteSending ? 0.8 : 1,
                }}
              >
                {inviteSending
                  ? t('adminPermissionsPage.invite.sending')
                  : t('adminPermissionsPage.invite.send')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )

  return <ResponsiveShell title={t('adminPermissionsPage.title')}>{content}</ResponsiveShell>
}
